import { Trophy, UserPlus, Users } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import DubLogo from '../components/DubLogo'
import { Button } from '../components/ui/button'
import { Card, CardContent } from '../components/ui/card'
import { useAuth } from '../contexts/AuthContext'
import { useNotification } from '../contexts/NotificationContext'
import {
  clearPendingGroupInvite,
  readPendingGroupInvite,
  savePendingGroupInvite,
} from '../lib/groupInvite'
import { fetchGroupInvitePreview, joinGroup } from '../services/groupService'

// Landing page for a group invite link (/join/:groupId). Public: a signed-out
// visitor sees the group preview and is sent to sign up, and the pending
// invite brings them back here to join once they log in.
export default function JoinGroup() {
  const { groupId } = useParams()
  const navigate = useNavigate()
  const notify = useNotification()
  const { user, loading: authLoading } = useAuth()

  const [preview, setPreview] = useState(null)
  const [loading, setLoading] = useState(true)
  const [joining, setJoining] = useState(false)
  const autoJoinTried = useRef(false)

  useEffect(() => {
    if (authLoading) return
    let cancelled = false
    setLoading(true)
    fetchGroupInvitePreview({ groupId })
      .then((data) => { if (!cancelled) setPreview(data || null) })
      .catch((err) => {
        console.error('Error loading group invite:', err)
        if (!cancelled) setPreview(null)
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [groupId, authLoading, user?.id])

  const handleJoin = async () => {
    setJoining(true)
    try {
      await joinGroup({ groupId })
      notify.success(`You joined "${preview.name}"!`)
    } catch (err) {
      // Joining twice is harmless: they end up in the group either way.
      if (!/already a member/i.test(err?.message || '')) {
        console.error('Error joining group:', err)
        notify.error(err?.message || 'Failed to join group')
        setJoining(false)
        return
      }
    }
    clearPendingGroupInvite()
    navigate(`/groups/${groupId}`, { replace: true })
  }

  useEffect(() => {
    if (loading || !preview || !user) return
    if (preview.is_member) {
      clearPendingGroupInvite()
      navigate(`/groups/${groupId}`, { replace: true })
      return
    }
    // Back from signup or login: finish the join they started.
    if (readPendingGroupInvite() === groupId && !autoJoinTried.current) {
      autoJoinTried.current = true
      handleJoin()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, preview, user, groupId])

  const goToAuth = (path) => {
    savePendingGroupInvite(groupId)
    navigate(path)
  }

  const memberCount = Number(preview?.member_count || 0)

  return (
    <div className="min-h-screen bg-background flex flex-col items-center px-4 py-10">
      <DubLogo className="h-8 mb-8" />

      <Card className="w-full max-w-md">
        <CardContent className="p-6 sm:p-8">
          {loading || authLoading ? (
            <div className="py-10 text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto" />
              <p className="mt-4 text-muted-foreground">Loading invite...</p>
            </div>
          ) : !preview ? (
            <div className="py-6 text-center">
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h1 className="text-xl font-semibold mb-2">This invite link doesn&apos;t work</h1>
              <p className="text-muted-foreground mb-6">
                The group may have been deleted. Ask the person who invited you for a new link.
              </p>
              <Button onClick={() => navigate(user ? '/groups' : '/register')}>
                {user ? 'Browse groups' : 'Sign up for dub'}
              </Button>
            </div>
          ) : (
            <div className="text-center">
              <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-primary" />
              </div>
              <p className="text-sm text-muted-foreground mb-1">
                {preview.owner_first_name
                  ? `${preview.owner_first_name} invited you to join`
                  : "You're invited to join"}
              </p>
              <h1 className="text-2xl font-bold break-words mb-2">{preview.name}</h1>
              {preview.description && (
                <p className="text-muted-foreground break-words mb-3">{preview.description}</p>
              )}
              <p className="text-sm text-muted-foreground flex items-center justify-center gap-1 mb-5">
                <Users className="h-4 w-4" />
                {memberCount} member{memberCount === 1 ? '' : 's'}
              </p>

              {preview.tournaments?.length > 0 && (
                <div className="text-left rounded-lg border bg-muted/30 p-4 mb-6">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
                    Tournaments in this group
                  </p>
                  <ul className="space-y-1.5">
                    {preview.tournaments.map((t) => (
                      <li key={t.id} className="flex items-center gap-2 text-sm">
                        <Trophy className="h-4 w-4 text-primary flex-shrink-0" />
                        <span className="break-words">{t.name}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {user ? (
                <Button variant="primaryNeon" className="w-full" onClick={handleJoin} disabled={joining}>
                  <UserPlus className="h-4 w-4 mr-2" />
                  {joining ? 'Joining...' : 'Join group'}
                </Button>
              ) : (
                <div className="space-y-3">
                  <Button variant="primaryNeon" className="w-full" onClick={() => goToAuth('/register')}>
                    Sign up to join
                  </Button>
                  <Button variant="outline" className="w-full" onClick={() => goToAuth('/login')}>
                    I already have an account
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    You&apos;ll join the group as soon as you&apos;re signed in.
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
